import { useEffect } from 'react';

/**
 * Book2Look's scripts, in order — the embedder needs the jQuery above it.
 *
 * headless loaded all three from `_app`'s `<Head>`, on every page of the site, to serve
 * the one page in a hundred that is a book with an isbn. They are loaded here instead,
 * after mount and only when there is a book to look inside.
 */
const SCRIPTS = [
  'https://www.book2look.com/bibletembedgeneratorv2/prod/js/jquery-3.6.0.min.js',
  'https://www.book2look.com/bibletembedgeneratorv2/prod/js/b2lbibletembeder.chunk.js',
  'https://www.book2look.com/bibletembedgeneratorv2/prod/js/b2lbibletembeder.js',
];

/**
 * The Book2Look account the isbn is looked up under. Not the current host: it is an
 * account identifier that happens to be spelled like a domain, so deriving it from
 * `window.location` would ask Book2Look for a book filed under `staging.discountmags.com`
 * and get nothing back.
 */
const ACCOUNT = 'discountmags.com';

/** The vendor script addresses its mount by id, and this is the id it expects. */
const HOLDER_ID = 'b2lbibletlayer_0';

let loading: Promise<void> | null = null;

function loadScripts(): Promise<void> {
  if (loading) {
    return loading;
  }

  loading = SCRIPTS.reduce(
    (chain, src) =>
      chain.then(
        () =>
          new Promise<void>((resolve, reject) => {
            if (document.querySelector(`script[src="${src}"]`)) {
              resolve();
              return;
            }

            const script = document.createElement('script');

            script.src = src;
            script.async = false;
            script.addEventListener('load', () => resolve());
            script.addEventListener('error', () => reject(new Error(`${src} failed to load`)));

            document.body.appendChild(script);
          })
      ),
    Promise.resolve()
  );

  // Only a *resolved* load is remembered. Memoizing a rejection would make one dropped
  // request permanent for the page, and the second mount would never retry.
  loading = loading.catch((error) => {
    loading = null;

    throw error;
  });

  return loading;
}

type Props = {
  isbn: string;
};

/**
 * The "Look Inside" pill over the bottom of the cover.
 *
 * The button is not ours: Book2Look's script draws it inside the div below and opens
 * its own full-screen reader when it is clicked, so all this renders is the mount and
 * all it configures is which book. Its styling lives in `styles/v2.css`, which is the
 * only way to reach markup React never sees.
 *
 * On headless the pill sat half off the bottom edge of the cover and slightly left of
 * centre — the holder was `width: 100%` *and* `translateX(-50%)`, and the pill inside
 * it relied on `margin: 0 auto` working on an element the vendor renders inline. The
 * port pins the holder to both edges and centres with flex, so it does not matter what
 * the vendor renders, and gives it a positive `bottom` so it sits on the cover rather
 * than under it. See `.b2lbiblet-holder` in v2.css.
 */
export default function LookInside({ isbn }: Props) {
  useEffect(() => {
    let cancelled = false;

    loadScripts()
      .then(() => {
        // Nothing to do if the mount was unmounted while the scripts were in flight, or
        // if the vendor has already drawn its button into it — asking twice gets two.
        if (cancelled || document.querySelector(`#${HOLDER_ID} .b2lButtonEmbed`)) {
          return;
        }

        const embedder = (window as any).B2LBibletEmbeder;

        // Order matters and is not optional. The script installs itself as
        // `B2LBibletEmbeder = B2LBibletEmbeder || { init }`, and `init()` is what takes
        // jQuery off noConflict, builds the lightbox and — the part that bites — sets
        // the `b2lCookie` its user-id lookup reads. Call readParams first and that
        // lookup throws ReferenceError on an undeclared global, from inside the vendor's
        // own code, with nothing on the page to show for it.
        if (typeof embedder?.init === 'function') {
          embedder.init();
        }

        const config = {
          divTag: HOLDER_ID,
          bookID: `${isbn}(${ACCOUNT})`,
          bibType: 'button',
          bibView: 'double',
          btnText: 'Look Inside',
          btnStyle: '',
        };

        // Replaces the object `init` came from: the script reads its configuration off
        // the global as well as off the argument, and it has already been initialized.
        (window as any).B2LBibletEmbeder = config;
        (window as any).readParams(config, `&id=${isbn}%28${encodeURIComponent(ACCOUNT)}%29`);
      })
      .catch((error) => {
        // A book with no preview, or a vendor outage. The cover is still a cover.
        console.error('Could not initialize the Look Inside widget', error);
      });

    return () => {
      cancelled = true;
    };
  }, [isbn]);

  return <div id={HOLDER_ID} className="b2lbiblet-holder" />;
}
